import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import {
  Globe,
  Smartphone,
  Brain,
  Zap,
  BarChart3,
  Megaphone,
} from "lucide-react";

const solutions = [
  {
    icon: Globe,
    title: "Custom Websites",
    description: "Responsive, fast, SEO-optimized",
    color: "from-[oklch(0.7_0.15_210)] to-[oklch(0.6_0.12_210)]",
  },
  {
    icon: Smartphone,
    title: "Mobile Apps",
    description: "iOS & Android native experience",
    color: "from-[oklch(0.7_0.15_330)] to-[oklch(0.6_0.12_330)]",
  },
  {
    icon: Brain,
    title: "AI Tools",
    description: "Chatbots, automation, insights",
    color: "from-[oklch(0.7_0.15_270)] to-[oklch(0.6_0.12_270)]",
  },
  {
    icon: Zap,
    title: "Automation",
    description: "Workflows, integrations, efficiency",
    color: "from-[oklch(0.7_0.15_180)] to-[oklch(0.6_0.12_180)]",
  },
  {
    icon: BarChart3,
    title: "Analytics",
    description: "Dashboards, reports, KPIs",
    color: "from-[oklch(0.7_0.15_150)] to-[oklch(0.6_0.12_150)]",
  },
  {
    icon: Megaphone,
    title: "Marketing Tools",
    description: "Campaigns, SEO, lead generation",
    color: "from-[oklch(0.7_0.15_30)] to-[oklch(0.6_0.12_30)]",
  },
];

export default function DeviceMockupsWithSolutions() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  // Device parallax transforms
  const desktopY = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const mobileY = useTransform(scrollYProgress, [0, 1], [-50, 50]);
  const tabletY = useTransform(scrollYProgress, [0, 1], [80, -80]);

  // Solution cards parallax (different speeds for depth)
  const card1Y = useTransform(scrollYProgress, [0, 1], [150, -150]);
  const card2Y = useTransform(scrollYProgress, [0, 1], [-100, 100]);
  const card3Y = useTransform(scrollYProgress, [0, 1], [120, -120]);
  const card4Y = useTransform(scrollYProgress, [0, 1], [-80, 80]);
  const card5Y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const card6Y = useTransform(scrollYProgress, [0, 1], [-120, 120]);

  const cardTransforms = [card1Y, card2Y, card3Y, card4Y, card5Y, card6Y];

  return (
    <section
      id="showcase"
      ref={containerRef}
      className="relative py-16 md:py-48 overflow-hidden bg-gradient-to-b from-background via-[oklch(0.18_0.02_215)] to-background"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[oklch(0.7_0.15_210)]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[oklch(0.7_0.15_330)]/10 rounded-full blur-3xl" />
      </div>

      <div className="container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-20"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Solutions That
            <span className="bg-gradient-to-r from-[oklch(0.7_0.15_210)] to-[oklch(0.7_0.15_330)] bg-clip-text text-transparent">
              {" "}
              Transform Business
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Enterprise-grade tools custom-built for small businesses. No templates, no compromises.
          </p>
        </motion.div>

        {/* Device Mockups with Floating Solution Cards */}
        <div className="relative min-h-[800px] md:min-h-[1000px]">
          {/* Solution Cards - Floating around mockups */}
          {solutions.map((solution, index) => {
            const Icon = solution.icon;
            const positions = [
              "top-[10%] left-[5%]", // Top left
              "top-[15%] right-[8%]", // Top right
              "top-[45%] left-[2%]", // Middle left
              "top-[50%] right-[5%]", // Middle right
              "bottom-[15%] left-[8%]", // Bottom left
              "bottom-[10%] right-[10%]", // Bottom right
            ];

            return (
              <motion.div
                key={index}
                style={{ y: cardTransforms[index] }}
                className={`absolute ${positions[index]} hidden lg:block z-30`}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="group relative">
                  {/* Glow effect */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${solution.color} opacity-20 blur-xl group-hover:opacity-30 transition-opacity rounded-2xl`}
                  />

                  {/* Card */}
                  <div className="relative bg-card/80 backdrop-blur-md border border-border/50 rounded-2xl p-6 w-64 hover:border-border transition-all">
                    <div
                      className={`w-12 h-12 rounded-xl bg-gradient-to-br ${solution.color} flex items-center justify-center mb-4`}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">
                      {solution.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {solution.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}

          {/* Desktop Mockup - Center */}
          <motion.div
            style={{ y: desktopY }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl z-10"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="relative">
              {/* Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-[oklch(0.7_0.15_210)] to-[oklch(0.7_0.15_330)] opacity-20 blur-3xl" />

              {/* Mockup */}
              <div className="relative">
                <img
                  src="/assets/mockup-desktop.webp"
                  alt="Desktop Application"
                  width="1536"
                  height="1024"
                  loading="eager"
                  fetchPriority="high"
                  className="w-full h-auto drop-shadow-2xl"
                />

                {/* Animated Overlays */}
                <div className="absolute top-[15%] left-[10%] w-[30%]">
                  <motion.div
                    animate={{ width: ["0%", "100%"] }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                    className="h-2 bg-gradient-to-r from-[oklch(0.7_0.15_210)] to-[oklch(0.7_0.15_330)] rounded-full"
                  />
                </div>

                {/* Pulsing data points */}
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={i}
                    animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.3,
                    }}
                    className="absolute w-3 h-3 bg-[oklch(0.7_0.15_330)] rounded-full"
                    style={{
                      top: `${30 + i * 15}%`,
                      right: `${15 + i * 5}%`,
                    }}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          {/* Mobile Mockup - Left */}
          <motion.div
            style={{ y: mobileY }}
            className="absolute top-[25%] left-[5%] md:left-[10%] w-32 md:w-48 z-5"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative">
              <div className="absolute inset-0 bg-[oklch(0.7_0.15_330)] opacity-20 blur-2xl" />
                <img
                  src="/assets/mockup-mobile.webp"
                  alt="Mobile Application"
                  width="1024"
                  height="1536"
                  loading="lazy"
                  className="w-full h-auto drop-shadow-2xl"
                />

              {/* Typing indicator */}
              <div className="absolute bottom-[20%] left-[20%] flex gap-1">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    animate={{ y: [0, -4, 0] }}
                    transition={{
                      duration: 0.6,
                      repeat: Infinity,
                      delay: i * 0.2,
                    }}
                    className="w-1.5 h-1.5 bg-[oklch(0.7_0.15_210)] rounded-full"
                  />
                ))}
              </div>

              {/* Notification badge */}
              <motion.div
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
                className="absolute top-[15%] right-[15%] w-4 h-4 bg-[oklch(0.7_0.15_330)] rounded-full border-2 border-background"
              />
            </div>
          </motion.div>

          {/* Tablet Mockup - Right */}
          <motion.div
            style={{ y: tabletY }}
            className="absolute bottom-[15%] right-[5%] md:right-[10%] w-48 md:w-64 z-5"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="relative">
              <div className="absolute inset-0 bg-[oklch(0.7_0.15_210)] opacity-20 blur-2xl" />
                <img
                  src="/assets/mockup-tablet.webp"
                  alt="Tablet Application"
                  width="1536"
                  height="1024"
                  loading="lazy"
                  className="w-full h-auto drop-shadow-2xl"
                />

              {/* Animated chart bars */}
              <div className="absolute bottom-[25%] left-[20%] flex items-end gap-1 h-12">
                {[60, 80, 45, 90, 70].map((height, i) => (
                  <motion.div
                    key={i}
                    animate={{ height: [`0%`, `${height}%`] }}
                    transition={{
                      duration: 1,
                      delay: i * 0.1,
                      repeat: Infinity,
                      repeatDelay: 3,
                    }}
                    className="w-2 bg-gradient-to-t from-[oklch(0.7_0.15_210)] to-[oklch(0.7_0.15_330)] rounded-t"
                  />
                ))}
              </div>

              {/* Floating stats */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute top-[20%] right-[15%] bg-[oklch(0.7_0.15_330)]/20 backdrop-blur-sm rounded-lg px-2 py-1 text-xs font-semibold"
              >
                +24%
              </motion.div>
            </div>
          </motion.div>

          {/* Mobile-only solution cards grid */}
          <div className="lg:hidden grid grid-cols-2 gap-4 mt-[600px] relative z-40">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group relative"
                >
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${solution.color} opacity-20 blur-xl group-hover:opacity-30 transition-opacity rounded-2xl`}
                  />
                  <div className="relative bg-card/80 backdrop-blur-md border border-border/50 rounded-2xl p-4 hover:border-border transition-all">
                    <div
                      className={`w-10 h-10 rounded-xl bg-gradient-to-br ${solution.color} flex items-center justify-center mb-3`}
                    >
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-base mb-1">
                      {solution.title}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {solution.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
